/**
 * Style overrides of custom components (owner feedback F21, level B): a builder changes how a node (or the whole
 * component) looks by choosing from the design system's own scales, never by typing a colour or a length. Every value
 * is a token name, so light and dark, the brand and the density of the workspace keep working. Pure, browser-safe.
 */
import { z } from 'zod';
import { type Lbl, L } from './custom-blocks';

export const SPACES = ['none', 'xs', 'sm', 'md', 'lg', 'xl'] as const;
export const RADII = ['none', 'sm', 'md', 'lg', 'xl', 'full'] as const;
/** Background tokens a node may take (all have a light and a dark value). */
export const SURFACES = ['canvas', 'surface', 'subtle', 'neutral-chip', 'accent-tint', 'accent', 'info-bg', 'ok-bg', 'warn-bg', 'err-bg', 'inverse'] as const;
/** Text and icon tokens (`on-accent` and `on-inverse` belong on the `accent` and `inverse` surfaces). */
export const INKS = ['ink', 'ink-2', 'ink-3', 'accent-ink', 'on-accent', 'ok', 'warn', 'err', 'info', 'on-inverse'] as const;
export const BORDERS = ['none', 'subtle', 'default', 'strong', 'accent'] as const;
export const SHADOWS = ['none', 'sm', 'md', 'lg'] as const;
export const FONTS = ['sans', 'mono'] as const;
export const TEXT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const;
export const WEIGHTS = ['regular', 'medium', 'semibold', 'bold'] as const;
export const TEXT_ALIGNS = ['start', 'center', 'end'] as const;

export const NodeStyle = z
  .object({
    pad: z.enum(SPACES),
    gap: z.enum(SPACES),
    radius: z.enum(RADII),
    surface: z.enum(SURFACES),
    ink: z.enum(INKS),
    border: z.enum(BORDERS),
    shadow: z.enum(SHADOWS),
    font: z.enum(FONTS),
    size: z.enum(TEXT_SIZES),
    weight: z.enum(WEIGHTS),
    align: z.enum(TEXT_ALIGNS),
  })
  .partial()
  .strict();
export type NodeStyle = z.infer<typeof NodeStyle>;

export const ACCENTS = ['indigo', 'blue', 'teal', 'green', 'amber', 'orange', 'rose', 'violet', 'slate'] as const;
export const DENSITIES = ['compact', 'comfortable', 'spacious'] as const;
export const THEME_RADII = ['sharp', 'default', 'soft', 'round'] as const;

/** The look of a whole component: one accent, the roundness, the density of spacing and the typeface. */
export const ComponentTheme = z
  .object({
    accent: z.enum(ACCENTS),
    radius: z.enum(THEME_RADII),
    density: z.enum(DENSITIES),
    font: z.enum(FONTS),
    surface: z.enum(['default', 'subtle', 'canvas']),
  })
  .partial()
  .strict();
export type ComponentTheme = z.infer<typeof ComponentTheme>;

export const STYLE_LABELS: Record<string, Lbl> = {
  pad: L('Padding', 'Innenabstand'),
  gap: L('Gap', 'Abstand'),
  radius: L('Corners', 'Ecken'),
  surface: L('Background', 'Hintergrund'),
  ink: L('Text colour', 'Textfarbe'),
  border: L('Border', 'Rahmen'),
  shadow: L('Shadow', 'Schatten'),
  font: L('Typeface', 'Schrift'),
  size: L('Text size', 'Textgröße'),
  weight: L('Weight', 'Stärke'),
  align: L('Alignment', 'Ausrichtung'),
  accent: L('Accent colour', 'Akzentfarbe'),
  density: L('Density', 'Dichte'),
};

/** Words of every value of the scales (the inspector shows them in both languages). */
export const STYLE_VALUE_LABELS: Record<string, Lbl> = {
  none: L('None', 'Keine'),
  xs: L('Extra small', 'Sehr klein'),
  sm: L('Small', 'Klein'),
  md: L('Medium', 'Mittel'),
  lg: L('Large', 'Groß'),
  xl: L('Extra large', 'Sehr groß'),
  '2xl': L('Huge', 'Riesig'),
  full: L('Round', 'Rund'),
  canvas: L('Canvas', 'Hintergrund'),
  surface: L('Surface', 'Fläche'),
  subtle: L('Subtle', 'Dezent'),
  'neutral-chip': L('Grey', 'Grau'),
  'accent-tint': L('Accent tint', 'Akzenttönung'),
  accent: L('Accent', 'Akzent'),
  'info-bg': L('Blue', 'Blau'),
  'ok-bg': L('Green', 'Grün'),
  'warn-bg': L('Amber', 'Bernstein'),
  'err-bg': L('Red', 'Rot'),
  inverse: L('Dark', 'Dunkel'),
  ink: L('Primary', 'Primär'),
  'ink-2': L('Secondary', 'Sekundär'),
  'ink-3': L('Muted', 'Gedämpft'),
  'accent-ink': L('Accent', 'Akzent'),
  'on-accent': L('On accent', 'Auf Akzent'),
  'on-inverse': L('On dark', 'Auf Dunkel'),
  ok: L('Green', 'Grün'),
  warn: L('Amber', 'Bernstein'),
  err: L('Red', 'Rot'),
  info: L('Blue', 'Blau'),
  default: L('Default', 'Standard'),
  strong: L('Strong', 'Kräftig'),
  sans: L('Sans', 'Serifenlos'),
  mono: L('Mono', 'Festbreite'),
  regular: L('Regular', 'Normal'),
  medium: L('Medium', 'Mittel'),
  semibold: L('Semibold', 'Halbfett'),
  bold: L('Bold', 'Fett'),
  start: L('Start', 'Links'),
  center: L('Centre', 'Mitte'),
  end: L('End', 'Rechts'),
  indigo: L('Indigo', 'Indigo'),
  blue: L('Blue', 'Blau'),
  teal: L('Teal', 'Petrol'),
  green: L('Green', 'Grün'),
  amber: L('Amber', 'Bernstein'),
  orange: L('Orange', 'Orange'),
  rose: L('Rose', 'Rosé'),
  violet: L('Violet', 'Violett'),
  slate: L('Slate', 'Schiefer'),
  compact: L('Compact', 'Kompakt'),
  comfortable: L('Comfortable', 'Normal'),
  spacious: L('Spacious', 'Luftig'),
  sharp: L('Sharp', 'Eckig'),
  soft: L('Soft', 'Weich'),
  round: L('Round', 'Rund'),
};
